type LoadingFlags = 'purchased_loading' | 'assigned_loading';

import { HttpErrorResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { IAllocatedAllotment, IAssignedAllotment, IManagementGroup, IProfile, IPurchasedAllotment, IResponse } from '@interfaces/index';
import { AuthorizationService } from '@ui-components/services';
import { of, switchMap, tap } from 'rxjs';
import { AllotmentService } from '../../services/allotment.service';
import { AppService } from '@ui-components/services';
import { AllocateComponent } from '../allocate/allocate.component';
import { ProductsComponent } from '../products/products.component';

@Component({
  selector: 'app-allotments',
  templateUrl: './allotments.component.html',
  styleUrl: './allotments.component.scss'
})
export class AllotmentsComponent {

  purchase_columns = ['name', 'purchased', 'allocated'];
  assign_columns = ['org', 'assessment', 'total', 'consumed', 'actions'];
  purchased: IPurchasedAllotment[] = [];
  allotments: IAssignedAllotment[] = [];
  purchased_loading = false;
  assigned_loading = false;
  profile: IProfile | null = null;
  selectedManagementGroup: IManagementGroup | null = null;

  get hasOrgAccess() {
    return this.profile ? [1, 2].includes(this.profile.role_id) : undefined;
  }

  get isSuperSuperAdmin(): boolean {
    return this._authorization_service.isSuperSuperAdmin(this.profile);
  }

  get isManagementGroupOwnedByUser(): boolean {
    return !!this.profile?.admin_org && this.profile?.org_id === this.selectedManagementGroup?.id;
  }

  get canViewProducts(): boolean {
    if (!this.profile) return false;
    if (this.isSuperSuperAdmin) return true;

    // Add the roles here that are able to purchase (currently just org super admin)
    return [1].includes(this.profile.role_id);
  }

  constructor(
    protected _allotment_service: AllotmentService,
    protected _app_service: AppService,
    protected _authorization_service: AuthorizationService,
    public dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this._app_service.selectedManagementGroup$.subscribe((group) => {
      this.selectedManagementGroup = group;
      this.loadAllotments();
    });

    this._app_service.getProfile().pipe(
      switchMap((response: IResponse<IProfile>) => {
        this.profile = response.data;

        if (!this.hasOrgAccess) {
          this.assign_columns.splice(0, 1);
        }
        if (![1, 2, 4].includes(this.profile.role_id)) {
          this.assign_columns.pop();
        }

        return this.hasOrgAccess ? this._allotment_service.getPurchasedAllotments() : of({ data: [], code: 200 });
      })
    ).subscribe(
      (response: IResponse<IPurchasedAllotment[]>) => {
        this.purchased = response.data;
        this.loadAllotments();
      },
      (error: HttpErrorResponse) => this.handleError(error)
    );
  }

  private handleError(error: HttpErrorResponse, loadingFlag?: LoadingFlags): void {
    this._app_service.notify('error', error.message);
    if (loadingFlag) {
      this[loadingFlag] = false;
    }
  }

  private loadAllotments(): void {
    if (!this.hasOrgAccess) {
      this.purchased = [];
      this.allotments = [];
      return;
    }

    this.purchased_loading = true;
    this.assigned_loading = true;

    this._allotment_service.getPurchasedAllotments().pipe(
      tap((response: IResponse<IPurchasedAllotment[]>) => {
        this.purchased = response.data;
        this.purchased_loading = false;
      }),
      switchMap(() => this._allotment_service.getAll())
    ).subscribe({
      next: (response: IResponse<IAssignedAllotment[]>) => {
        this.allotments = response.data;
        this.assigned_loading = false;
      },
      error: (error: HttpErrorResponse) => this.handleError(error, 'assigned_loading'),
    });
  }

  showProducts(): void {
    if (!this.canViewProducts) {
      return;
    }

    const dialog_ref = this.dialog.open(ProductsComponent, {
      width: '600px',
    });

    dialog_ref.afterClosed().subscribe((reload: boolean) => {
      if (reload) {
        this.loadAllotments();
      }
    });
  }

  showAllocate(purchased: IPurchasedAllotment[]): void {
    const dialog_ref = this.dialog.open(AllocateComponent, {
      width: '600px',
      data: purchased
    });

    dialog_ref.afterClosed().pipe(
      switchMap((reload: boolean) => {
        if (reload) {
          this.purchased_loading = true;
          return this._allotment_service.getPurchasedAllotments();
        }
        return of({ data: purchased, code: 200 });
      })
    ).subscribe(
      (response: IResponse<IPurchasedAllotment[]>) => {
        this.purchased = response.data;
        this.purchased_loading = false;
        this.loadAllotments();
      },
      (error: HttpErrorResponse) => this.handleError(error, 'purchased_loading')
    );
  }

  unallocate(assessment: IAllocatedAllotment): void {
    if (this.hasOrgAccess) {
      const left_over = assessment.assigned - assessment.used;
      if (left_over === 0) {
        this._app_service.notify('error', 'All the assessments have been used, there are none left to unallocate!');
        return;
      }

      this._allotment_service.unallocate(assessment).pipe(
        tap(() => this.loadAllotments())
      ).subscribe({
        next: () => {
          this._app_service.notify('success', 'Unallocated successfully.');
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, 'assigned_loading');
        },
      });
    }
  }

  assign(allotment: IAssignedAllotment): void {
    const org_id = allotment.org_id;
    const assessment_id = allotment.assessment_id
    const url = `invite/?org_id=${encodeURIComponent(org_id)}&assessment_id=${encodeURIComponent(assessment_id)}`;
    window.location.href = url;
  }


}
